import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUpdateOrganizationSourcePartner,
  useAdminUpdateOrganizationCurrentPartner,
  getAdminGetOrganizationQueryKey,
} from '@/api/auth/admin/admin';
import type { AdminOrganizationResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OrgLookupCombobox } from './OrgLookupCombobox';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

interface PartnerAssociationsFormProps {
  organization: AdminOrganizationResponse;
}

/**
 * Edits the Source and Current partner associations via their dedicated admin
 * endpoints (kept separate from the general update per the Partner Association
 * spec). Each picker lists partner organizations; selecting "None" clears the
 * association. The server validates the references and invariants.
 */
export function PartnerAssociationsForm({ organization }: PartnerAssociationsFormProps) {
  const queryClient = useQueryClient();

  const [sourceId, setSourceId] = useState<string | null>(organization.sourcePartnerId ?? null);
  const [sourceReason, setSourceReason] = useState('');
  const [currentId, setCurrentId] = useState<string | null>(organization.currentPartnerId ?? null);
  const [currentReason, setCurrentReason] = useState('');

  // Re-sync each picker when the server value changes after a refetch, e.g. when setting the
  // current partner also backfills an empty source partner. Unsaved edits to the other picker survive.
  useEffect(() => {
    setSourceId(organization.sourcePartnerId ?? null);
  }, [organization.sourcePartnerId]);

  useEffect(() => {
    setCurrentId(organization.currentPartnerId ?? null);
  }, [organization.currentPartnerId]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getAdminGetOrganizationQueryKey(organization.id),
    });

  const handleError = (context: string) => (error: any) => {
    logger.error(error, { context, orgId: organization.id });
    toast.error('Update failed. ' + (error.response?.data?.detail || error.message));
  };

  const sourceMutation = useAdminUpdateOrganizationSourcePartner({
    mutation: {
      onSuccess: () => {
        toast.success('Source partner updated');
        setSourceReason('');
        invalidate();
      },
      onError: handleError('UpdateOrganizationSourcePartner'),
    },
  });

  const currentMutation = useAdminUpdateOrganizationCurrentPartner({
    mutation: {
      onSuccess: () => {
        toast.success('Current partner updated');
        setCurrentReason('');
        invalidate();
      },
      onError: handleError('UpdateOrganizationCurrentPartner'),
    },
  });

  const sourceChanged = (sourceId ?? null) !== (organization.sourcePartnerId ?? null);
  const currentChanged = (currentId ?? null) !== (organization.currentPartnerId ?? null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Partner Associations</CardTitle>
        <CardDescription>
          Source and Current partner must reference a partner organization. Selecting "None" clears
          the association.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Source partner</Label>
          {organization.isPartner ? (
            <p className="text-sm text-muted-foreground">
              A partner organization cannot have a source partner.
            </p>
          ) : (
            <>
              <OrgLookupCombobox
                value={sourceId}
                onSelect={setSourceId}
                excludeId={organization.id}
                disabled={sourceMutation.isPending}
              />
              <Input
                placeholder="Reason (optional)"
                value={sourceReason}
                onChange={(e) => setSourceReason(e.target.value)}
                disabled={sourceMutation.isPending}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={!sourceChanged || sourceMutation.isPending}
                  onClick={() =>
                    sourceMutation.mutate({
                      orgId: organization.id,
                      data: { sourcePartnerId: sourceId, reason: sourceReason || undefined },
                    })
                  }
                >
                  Save source partner
                </Button>
                {sourceChanged && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={sourceMutation.isPending}
                    onClick={() => setSourceId(organization.sourcePartnerId ?? null)}
                  >
                    Reset
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="space-y-2">
          <Label>Current partner</Label>
          <OrgLookupCombobox
            value={currentId}
            onSelect={setCurrentId}
            excludeId={organization.id}
            disabled={currentMutation.isPending}
          />
          <Input
            placeholder="Reason (optional)"
            value={currentReason}
            onChange={(e) => setCurrentReason(e.target.value)}
            disabled={currentMutation.isPending}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!currentChanged || currentMutation.isPending}
              onClick={() =>
                currentMutation.mutate({
                  orgId: organization.id,
                  data: { currentPartnerId: currentId, reason: currentReason || undefined },
                })
              }
            >
              Save current partner
            </Button>
            {currentChanged && (
              <Button
                size="sm"
                variant="ghost"
                disabled={currentMutation.isPending}
                onClick={() => setCurrentId(organization.currentPartnerId ?? null)}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
